/**
 * FolderTree Component
 * Recursive tree view for hierarchical folder navigation
 * Features: Expand/collapse, selection, context menu
 */

import React, { useState } from 'react';
import { Folder } from '../services/api';
import './FolderTree.css';

interface FolderTreeProps {
  folders: Folder[];
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string) => void;
  onCreateSubfolder: (parentId: string) => void;
  onRenameFolder: (folderId: string) => void;
  onDeleteFolder: (folderId: string) => void;
  title?: string;
}

interface FolderNodeProps {
  folder: Folder;
  level: number;
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string) => void;
  onCreateSubfolder: (parentId: string) => void;
  onRenameFolder: (folderId: string) => void;
  onDeleteFolder: (folderId: string) => void;
  children: Folder[];
  allFolders: Folder[];
}

// Compute recursive count for a folder (sum of direct counts of all descendants)
function getRecursiveCount(folderId: string, allFolders: Folder[], countKey: 'test_case_count' | 'test_scenario_count'): number {
  let total = 0;
  const queue = [folderId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const folder = allFolders.find(f => f.id === current);
    if (folder) {
      total += (folder[countKey] as number) || 0;
      allFolders.filter(f => f.parent_id === current).forEach(c => queue.push(c.id));
    }
  }
  return total;
}

const FolderNode: React.FC<FolderNodeProps> = ({
  folder,
  level,
  selectedFolderId,
  onSelectFolder,
  onCreateSubfolder,
  onRenameFolder,
  onDeleteFolder,
  children,
  allFolders
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });

  const hasChildren = children.length > 0;
  const isSelected = folder.id === selectedFolderId;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleSelect = () => {
    onSelectFolder(folder.id);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const handleCloseContextMenu = () => {
    setShowContextMenu(false);
  };

  const handleCreateSubfolder = () => {
    onCreateSubfolder(folder.id);
    handleCloseContextMenu();
  };

  const handleRename = () => {
    onRenameFolder(folder.id);
    handleCloseContextMenu();
  };

  const handleDelete = () => {
    onDeleteFolder(folder.id);
    handleCloseContextMenu();
  };

  return (
    <div className="folder-node-wrapper">
      <div
        className={`folder-node ${isSelected ? 'selected' : ''}`}
        style={{ paddingLeft: `${level * 20}px` }}
        onClick={handleSelect}
        onContextMenu={handleContextMenu}
      >
        <div className="folder-node-content">
          {hasChildren && (
            <button
              className="folder-toggle"
              onClick={handleToggle}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          {!hasChildren && <span className="folder-spacer"></span>}
          
          <span className="folder-icon">
            {isExpanded && hasChildren ? '📂' : '📁'}
          </span>
          
          <span className="folder-name" title={folder.name}>
            {folder.name}
            {folder.folder_type === 'test_case' && folder.level > 0 && (() => {
              const count = getRecursiveCount(folder.id, allFolders, 'test_case_count');
              return count > 0 ? <span className="folder-count"> ({count})</span> : null;
            })()}
            {folder.folder_type === 'test_scenario' && folder.level > 0 && (() => {
              const count = getRecursiveCount(folder.id, allFolders, 'test_scenario_count');
              return count > 0 ? <span className="folder-count"> ({count})</span> : null;
            })()}
          </span>
        </div>
      </div>

      {showContextMenu && (
        <>
          <div className="context-menu-overlay" onClick={handleCloseContextMenu} />
          <div
            className="folder-context-menu"
            style={{ top: contextMenuPos.y, left: contextMenuPos.x }}
          >
            <button onClick={handleCreateSubfolder} className="context-menu-item">
              <span className="context-menu-icon">➕</span>
              Create Subfolder
            </button>
            <button onClick={handleRename} className="context-menu-item">
              <span className="context-menu-icon">✏️</span>
              Rename
            </button>
            <div className="context-menu-divider" />
            <button onClick={handleDelete} className="context-menu-item danger">
              <span className="context-menu-icon">🗑️</span>
              Delete
            </button>
          </div>
        </>
      )}

      {isExpanded && hasChildren && (
        <div className="folder-children">
          {children.map(child => {
            const childChildren = allFolders.filter(f => f.parent_id === child.id);
            return (
              <FolderNode
                key={child.id}
                folder={child}
                level={level + 1}
                selectedFolderId={selectedFolderId}
                onSelectFolder={onSelectFolder}
                onCreateSubfolder={onCreateSubfolder}
                onRenameFolder={onRenameFolder}
                onDeleteFolder={onDeleteFolder}
                children={childChildren}
                allFolders={allFolders}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export const FolderTree: React.FC<FolderTreeProps> = ({
  folders,
  selectedFolderId,
  onSelectFolder,
  onCreateSubfolder,
  onRenameFolder,
  onDeleteFolder,
  title = "Projects"
}) => {
  // Build tree structure - get children for a given parent
  const getChildren = (parentId: string | null): Folder[] => {
    return folders.filter(f => f.parent_id === parentId);
  };

  const rootFolders = getChildren(null);

  console.log('FolderTree render:', {
    totalFolders: folders.length,
    rootFolders: rootFolders.length,
    allFolders: folders.map(f => ({ id: f.id, name: f.name, parent_id: f.parent_id, level: f.level }))
  });

  return (
    <div className="folder-tree">
      <div className="folder-tree-header">
        <h3>📁 {title}</h3>
      </div>
      <div className="folder-tree-content">
        {rootFolders.length === 0 ? (
          <div className="folder-tree-empty">
            <p>No {title.toLowerCase()} found</p>
            <p style={{ fontSize: '11px', color: '#999' }}>Total folders: {folders.length}</p>
          </div>
        ) : (
          <>
            {rootFolders.map(folder => {
              const children = getChildren(folder.id);
              return (
                <FolderNode
                  key={folder.id}
                  folder={folder}
                  level={0}
                  selectedFolderId={selectedFolderId}
                  onSelectFolder={onSelectFolder}
                  onCreateSubfolder={onCreateSubfolder}
                  onRenameFolder={onRenameFolder}
                  onDeleteFolder={onDeleteFolder}
                  children={children}
                  allFolders={folders}
                />
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};
